import Image from 'next/image'
import { Badge, Nameplate, TileCaption, TileOverlay } from '@mc/design-system'
import { cloudflareLoader } from '@mc/image-cloudflare'
import { EventTypes, track } from '@mc/track-event'
import { nameplateOptimizedImage } from 'components/GlobalCarousel'
import Link from 'components/Link'
import { type CarouselItemType } from '../constants'
import { useSpringboardTileHref } from '../hooks'

type SpringboardCarouselTileProps = {
  uuid: string
  slug: string
  itemType?: CarouselItemType
  imageURL: string
  height: number
  width: number
  title: string
  nameplateImage?: string
  showCategoryBadge?: boolean
  category?: string
  categoryId?: number
  size?: 'sm' | 'lg'
}

export const SpringboardCarouselTile = ({
  uuid,
  itemType,
  imageURL,
  title,
  slug,
  nameplateImage,
  showCategoryBadge = false,
  category,
  categoryId,
  size = 'lg',
  ...imageProps
}: SpringboardCarouselTileProps) => {
  const href = useSpringboardTileHref(uuid, itemType, categoryId)

  return (
    <Link
      href={href}
      onClick={() =>
        track(EventTypes.HOMEPAGE_CLICK, {
          action: 'click',
          location: 'carousel',
          class: slug,
          link: href,
        })
      }
    >
      <Image
        {...imageProps}
        src={imageURL}
        alt={title}
        loader={cloudflareLoader}
        className='mc-corners--md'
        priority
        style={{ objectFit: 'cover', height: '100%', maxWidth: '100%' }}
      />
      <TileOverlay
        className='mc-corners--md'
        color='34, 35, 38'
        type='gradient-bottom'
      />
      <TileCaption position='center bottom' className='mc-pb-5'>
        <div
          className={`d-flex flex-column mc-gap-3 align-items-center ${size === 'lg' ? 'mc-px-4' : ''}`}
        >
          {nameplateImage ? (
            <Nameplate
              className={size === 'lg' ? 'mc-mx-7' : 'mc-mx-3'}
              size='small'
              src={nameplateOptimizedImage(nameplateImage)}
              data-testid='nameplateImage'
            />
          ) : (
            <p
              className='mc-mb-2 mc-text--uppercase mc-text-b1'
              style={{
                fontSize: '36px',
                letterSpacing: '0.01em',
                lineHeight: '0.9',
                ...(size === 'sm'
                  ? { fontSize: '24px', lineHeight: '1.2' }
                  : {}),
              }}
            >
              {title}
            </p>
          )}
          {showCategoryBadge && (
            <Badge className='mc-text--normal' kind='transparent'>
              {category}
            </Badge>
          )}
        </div>
      </TileCaption>
    </Link>
  )
}
